/**
 * Transactions Browser — Phase A (read-only)
 *
 * Master list of bank/card transactions across amex/revolut/wise/paypal,
 * with linked-JE status (pending / journaled / duplicate / voided) and GL
 * account mapping. Click a row to see raw fields + every linked JE.
 *
 * Lives as a tab inside ReconciliationHub. Phase B (single-txn remap),
 * Phase C (bulk + rules), and Phase D (reconciliation dashboard) extend
 * this component without restructuring.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Drawer,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { UnconvertedFxBanner } from '../UnconvertedFxNotice';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ContentCopy as DuplicateIcon,
  Block as VoidedIcon,
  AutoFixHigh as ResolveIcon,
  Restore as RestoreIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';

import {
  listTransactions,
  getTransactionDetail,
  getTransactionsSummary,
  voidDuplicate,
  restoreDuplicate
} from '../../../services/transactionsService';

const SOURCES = ['amex', 'revolut', 'wise', 'paypal'];

// Must be the queue's OWN direction values — these go straight to the API and are
// compared to unmatched_bank_transactions.direction. This dropdown used to send
// 'inflow'/'outflow', which exist nowhere in that column, so either selection
// returned zero rows. 'transfer' (internal movement, e.g. an amex balance payment)
// and 'refund' get their own entries rather than being folded into in/out — see
// DIRECTION_FILTERS in backend/finance/transactions/routes.js for why.
const DIRECTIONS = [
  { value: 'outgoing', label: 'Outgoing (money out)' },
  { value: 'incoming', label: 'Incoming (money in)' },
  { value: 'transfer', label: 'Transfer (internal)' },
  { value: 'refund',   label: 'Refund' }
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'warning', icon: ScheduleIcon },
  journaled: { label: 'Journaled', color: 'success', icon: CheckCircleIcon },
  duplicate: { label: 'Duplicate', color: 'error', icon: DuplicateIcon },
  voided: { label: 'Voided', color: 'default', icon: VoidedIcon },
  // PayPal movements that cannot be an expense, so they are not classification
  // work: incoming customer payments (revenue — booked by order_revenue, not
  // per transaction) and hold / reserve / currency-conversion legs (PayPal
  // moving our own money between buckets of one account, both legs in the feed).
  //
  // The label says "Not an expense" and NOT "no entry needed", deliberately. For
  // the internal legs no entry is genuinely owed — they net to zero. For customer
  // receipts this bucket only asserts they are not a COST; whether the revenue is
  // recognised is a different question this queue cannot answer, and for the
  // 14,502 Wix-era receipts it is an open one (zero Wix-sourced journal entries
  // exist). Claiming "no entry needed" there would be a claim we cannot support.
  not_expense: { label: 'Not an expense', color: 'info', icon: InfoOutlinedIcon }
};

function formatCurrency(amount, currency) {
  if (amount === null || amount === undefined || isNaN(amount)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2
    }).format(amount);
  } catch (e) {
    return `${currency || ''} ${parseFloat(amount).toFixed(2)}`;
  }
}

function formatDate(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toISOString().slice(0, 10);
  } catch (e) {
    return String(dt).slice(0, 10);
  }
}

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'default' };
  const Icon = cfg.icon;
  return (
    <Chip
      size="small"
      label={cfg.label}
      color={cfg.color}
      icon={Icon ? <Icon sx={{ fontSize: 14 }} /> : undefined}
      variant={status === 'duplicate' ? 'filled' : 'outlined'}
    />
  );
}

function buildDateDefaults() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10)
  };
}

export default function TransactionsBrowser() {
  const dateDefaults = useMemo(buildDateDefaults, []);
  const [searchParams] = useSearchParams();

  // URL params (from BS/CFS deep-links): ?account_code=1010&bank_source=revolut&status=pending
  const urlAccountCode = searchParams.get('account_code') || '';
  const urlBankSource = searchParams.get('bank_source') || '';
  const urlStatus = searchParams.get('status') || '';
  // ?search=<memo or counterparty> — the filter already existed in state and was
  // already sent to the API; it just could not be set from a URL, so nothing could
  // link INTO a counterparty's transactions. Finance > Search links here.
  const urlSearch = searchParams.get('search') || '';
  // ?queue_id=<uuid> — opens that row's evidence drawer on mount. The drawer and its
  // loader already existed; only the entry point was missing, which made a single
  // transaction unlinkable from anywhere else in the app.
  const urlQueueId = searchParams.get('queue_id') || '';
  // A deep link to one transaction must not be hidden by the default 3-month window.
  const deepLinked = Boolean(urlQueueId);

  const [filters, setFilters] = useState({
    bank_source: urlBankSource,
    direction: '',
    status: urlStatus,
    start_date: deepLinked ? '' : dateDefaults.start,
    end_date: deepLinked ? '' : dateDefaults.end,
    search: urlSearch,
    account_code: urlAccountCode,
    duplicates_only: false
  });

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [summary, setSummary] = useState({ by_source: [], totals: { total: 0, pending: 0, journaled: 0, duplicate: 0, not_expense: 0 } });

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerError, setDrawerError] = useState(null);

  // Resolve-duplicate dialog state (Phase B v1)
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveReason, setResolveReason] = useState('');
  const [resolvePreview, setResolvePreview] = useState(null);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const [resolveSuccess, setResolveSuccess] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: pageSize,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        bank_source: filters.bank_source || undefined,
        direction: filters.direction || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
        account_code: filters.account_code || undefined,
        duplicates_only: filters.duplicates_only ? '1' : undefined,
        sort_by: 'date',
        sort_dir: 'desc'
      };
      const res = await listTransactions(params);
      if (res.success) {
        setRows(res.data || []);
        setPagination(res.pagination || { page, page_size: pageSize, total: 0, total_pages: 0 });
      } else {
        setError(res.error?.message || 'Failed to load transactions');
      }
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getTransactionsSummary({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined
      });
      if (res.success) setSummary(res.data);
    } catch (e) {
      // Non-fatal — summary chips just won't show counts
      console.warn('[Transactions] summary fetch failed:', e.message);
    }
  }, [filters.start_date, filters.end_date]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (_e, newPage) => setPage(newPage + 1);
  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const openDetail = async (queueId) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerError(null);
    setDrawerData(null);
    try {
      const res = await getTransactionDetail(queueId);
      if (res.success) {
        setDrawerData(res.data);
      } else {
        setDrawerError(res.error?.message || 'Failed to load transaction');
      }
    } catch (e) {
      setDrawerError(e.response?.data?.error?.message || e.message || 'Network error');
    } finally {
      setDrawerLoading(false);
    }
  };

  // ?queue_id=<uuid> — open that transaction's evidence drawer once on mount.
  // Deliberately not in the render-time filter set: the drawer loads the row by id
  // directly, so it works even when the row is outside the current list page or
  // date window. Runs once; closing the drawer must not re-open it.
  const deepLinkOpened = React.useRef(false);
  useEffect(() => {
    if (urlQueueId && !deepLinkOpened.current) {
      deepLinkOpened.current = true;
      openDetail(urlQueueId);
    }
    // openDetail is stable enough for a once-guarded mount effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQueueId]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerData(null);
    setDrawerError(null);
    setResolveDialogOpen(false);
    setResolveReason('');
    setResolvePreview(null);
    setResolveError(null);
    setResolveSuccess(null);
  };

  // Phase B v1: open resolve-dup dialog with a dry-run preview from the server.
  const openResolveDialog = async () => {
    if (!drawerData) return;
    setResolveBusy(true);
    setResolveError(null);
    setResolvePreview(null);
    try {
      // Server skips reason validation when dry_run is set — but we send a tag anyway for log clarity.
      const res = await voidDuplicate(drawerData.transaction.queue_id, {
        dry_run: true,
        reason: 'preview-dryrun'
      });
      if (res.success) {
        setResolvePreview(res);
        setResolveDialogOpen(true);
      } else {
        setResolveError(res.error?.message || 'Preview failed');
      }
    } catch (e) {
      setResolveError(e.response?.data?.error?.message || e.message || 'Network error');
    } finally {
      setResolveBusy(false);
    }
  };

  const confirmResolve = async () => {
    if (!drawerData || !resolveReason || resolveReason.trim().length < 8) return;
    setResolveBusy(true);
    setResolveError(null);
    try {
      const res = await voidDuplicate(drawerData.transaction.queue_id, { reason: resolveReason.trim() });
      if (res.success) {
        const voidedList = (res.voided_je_ids || (res.voided_je_id ? [res.voided_je_id] : [])).map(i => `#${i}`).join(', ');
        const detail = res.action === 'repost'
          ? `Voided JE ${voidedList}. Posted new canonical JE #${res.new_je_id}.`
          : `Voided JE ${voidedList}. Kept JE #${res.kept_je_id || res.keep_je_id}.`;
        setResolveSuccess(detail);
        setResolveDialogOpen(false);
        // Refresh drawer + list so the change is reflected
        await openDetail(drawerData.transaction.queue_id);
        fetchList();
        fetchSummary();
      } else {
        setResolveError(res.error?.message || 'Void failed');
      }
    } catch (e) {
      setResolveError(e.response?.data?.error?.message || e.message || 'Network error');
    } finally {
      setResolveBusy(false);
    }
  };

  const handleRestore = async (jeId) => {
    if (!drawerData) return;
    if (!window.confirm(`Restore voided JE #${jeId}?`)) return;
    setResolveBusy(true);
    setResolveError(null);
    try {
      const res = await restoreDuplicate(drawerData.transaction.queue_id, jeId);
      if (res.success) {
        setResolveSuccess(`Restored JE #${res.restored_je_id}.`);
        await openDetail(drawerData.transaction.queue_id);
        fetchList();
        fetchSummary();
      } else {
        setResolveError(res.error?.message || 'Restore failed');
      }
    } catch (e) {
      setResolveError(e.response?.data?.error?.message || e.message || 'Network error');
    } finally {
      setResolveBusy(false);
    }
  };

  const totalRows = pagination.total || 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight="600">All Transactions</Typography>
          <Typography variant="caption" color="text.secondary">
            Read-only browser across amex / revolut / wise / paypal. Click a row for raw fields and linked journal entries.
          </Typography>
        </Box>
        <IconButton onClick={() => { fetchList(); fetchSummary(); }} disabled={loading} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Quick-filter chips by status */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          label={`All ${summary.totals.total.toLocaleString()}`}
          variant={!filters.status && !filters.duplicates_only ? 'filled' : 'outlined'}
          onClick={() => { handleFilterChange('status', ''); handleFilterChange('duplicates_only', false); }}
        />
        <Chip
          icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
          label={`Pending ${summary.totals.pending.toLocaleString()}`}
          color="warning"
          variant={filters.status === 'pending' ? 'filled' : 'outlined'}
          onClick={() => handleFilterChange('status', 'pending')}
        />
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label={`Journaled ${summary.totals.journaled.toLocaleString()}`}
          color="success"
          variant={filters.status === 'journaled' ? 'filled' : 'outlined'}
          onClick={() => handleFilterChange('status', 'journaled')}
        />
        <Chip
          icon={<DuplicateIcon sx={{ fontSize: 14 }} />}
          label={`Duplicates ${summary.totals.duplicate.toLocaleString()}`}
          color="error"
          variant={filters.status === 'duplicate' || filters.duplicates_only ? 'filled' : 'outlined'}
          onClick={() => handleFilterChange('status', 'duplicate')}
        />
        <Chip
          icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
          label={`Not an expense ${(summary.totals.not_expense || 0).toLocaleString()}`}
          color="info"
          variant={filters.status === 'not_expense' ? 'filled' : 'outlined'}
          onClick={() => handleFilterChange('status', 'not_expense')}
        />
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" label="Start date" type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" label="End date" type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select fullWidth size="small" label="Source"
              value={filters.bank_source}
              onChange={(e) => handleFilterChange('bank_source', e.target.value)}
            >
              <MenuItem value="">All sources</MenuItem>
              {SOURCES.map(s => (
                <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select fullWidth size="small" label="Direction"
              value={filters.direction}
              onChange={(e) => handleFilterChange('direction', e.target.value)}
            >
              <MenuItem value="">All directions</MenuItem>
              {DIRECTIONS.map(d => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" label="GL account code"
              placeholder="e.g. 8020"
              value={filters.account_code}
              onChange={(e) => handleFilterChange('account_code', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" label="Search memo / counterparty"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* DT-1478: rows on this page with no USD rate for their month (amount_usd left unconverted). */}
      <UnconvertedFxBanner
        targetCurrency="USD"
        groups={[{
          label: 'Transactions on this page',
          unit: 'transactions',
          count: rows.filter(r => Number(r.fx_at_par) === 1).length,
          byCurrency: rows.filter(r => Number(r.fx_at_par) === 1).reduce((m, r) => {
            const k = r.currency || '';
            m[k] = (m[k] || 0) + Math.abs(Number(r.amount || 0));
            return m;
          }, {}),
        }]}
      />

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Counterparty / Memo</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>GL Account(s)</TableCell>
                <TableCell align="center" sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No transactions match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((row) => (
                <TableRow
                  key={row.queue_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetail(row.queue_id)}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.txn_date)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.bank_source} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="body2" noWrap>
                      {row.counterparty_name || row.memo || '—'}
                    </Typography>
                    {row.memo && row.counterparty_name && row.memo !== row.counterparty_name && (
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {row.memo}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(row.amount, row.currency)}
                    {Number(row.fx_at_par) === 1 && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>no USD rate: not converted</Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.direction || '—'}</TableCell>
                  <TableCell>
                    <StatusChip status={row.derived_status} />
                    {row.active_je_count > 1 && (
                      <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
                        {row.active_je_count}×
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.account_codes && row.account_codes.length > 0 ? (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                        {[...new Set(row.account_codes)].slice(0, 3).map((c, i) => {
                          const idx = row.account_codes.indexOf(c);
                          const name = row.account_names && row.account_names[idx] ? row.account_names[idx] : c;
                          return (
                            <Tooltip key={`${c}-${i}`} title={name}>
                              <Chip size="small" label={c} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDetail(row.queue_id); }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRows}
          page={Math.max(0, (pagination.page || 1) - 1)}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[25, 50, 100, 250]}
        />
      </Paper>

      {/* Detail drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight="600">Transaction Detail</Typography>
            <IconButton onClick={closeDrawer}><CloseIcon /></IconButton>
          </Box>

          {drawerLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
          {drawerError && <Alert severity="error">{drawerError}</Alert>}

          {drawerData && (
            <Box>
              {/* Summary card */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Counterparty</Typography>
                    <Typography variant="body2" fontWeight="600">{drawerData.transaction.counterparty_name || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body2">{formatDate(drawerData.transaction.txn_date)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Amount</Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formatCurrency(drawerData.transaction.amount, drawerData.transaction.currency)} {drawerData.transaction.direction}
                    </Typography>
                  </Box>
                  {Number(drawerData.transaction.fx_rate_missing) === 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">USD amount</Typography>
                      <Typography variant="body2" color="warning.main">
                        not converted (no {drawerData.transaction.currency || 'blank-currency'} to USD rate for this month)
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Source</Typography>
                    <Typography variant="body2"><Chip size="small" label={drawerData.transaction.bank_source} variant="outlined" /></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Bank account</Typography>
                    <Typography variant="body2">{drawerData.transaction.bank_account_name || drawerData.transaction.bank_account_id || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Memo</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: 400 }}>{drawerData.transaction.memo || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Queue status</Typography>
                    <Typography variant="body2">{drawerData.transaction.queue_status || '—'}</Typography>
                  </Box>
                  {drawerData.transaction.ai_classification && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">AI suggestion</Typography>
                      <Typography variant="body2">{drawerData.transaction.ai_classification} ({drawerData.transaction.ai_confidence || 0}%)</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>

              {/* Linked JEs */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Journal Entries ({drawerData.journal_entries.length})
              </Typography>

              {drawerData.journal_entries.length === 0 && (
                <Alert severity="info" icon={<ScheduleIcon />}>
                  No journal entries linked yet — this transaction is in the pending queue.
                </Alert>
              )}

              {drawerData.journal_entries.map((je) => {
                const isVoided = je.is_deleted === 1 || je.is_superseded === 1 || je.status === 'voided';
                return (
                  <Paper
                    key={je.je_id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderColor: isVoided ? 'grey.400' : 'primary.light',
                      bgcolor: isVoided ? 'grey.50' : 'background.paper',
                      opacity: isVoided ? 0.7 : 1
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box>
                        <Typography variant="body2" fontWeight="600">JE #{je.je_id}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {je.source_type} / {je.source_table}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(je.entry_date)} · {je.created_by}
                        </Typography>
                        {isVoided && <Chip size="small" label="Voided" sx={{ ml: 1 }} />}
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {je.memo}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Table size="small">
                      <TableBody>
                        {je.lines && je.lines.map((line, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', borderBottom: 'none', pl: 0 }}>
                              {line.account_code}
                            </TableCell>
                            <TableCell sx={{ borderBottom: 'none' }}>{line.account_name}</TableCell>
                            <TableCell align="right" sx={{ borderBottom: 'none', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {line.debit_amount > 0 ? (
                                <span>Dr {formatCurrency(line.debit_amount, je.base_currency)}</span>
                              ) : (
                                <span style={{ color: '#666' }}>Cr {formatCurrency(line.credit_amount, je.base_currency)}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                );
              })}

              {drawerData.journal_entries.filter(j => j.is_deleted === 0 && j.is_superseded === 0 && j.status === 'posted').length >= 2 && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{ mt: 2 }}
                  action={
                    drawerData.transaction.bank_source === 'amex' && (
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        startIcon={<ResolveIcon />}
                        disabled={resolveBusy}
                        onClick={openResolveDialog}
                      >
                        Resolve duplicate
                      </Button>
                    )
                  }
                >
                  This transaction has multiple active journal entries — likely a duplicate booking.
                  {drawerData.transaction.bank_source === 'amex'
                    ? ' Click "Resolve duplicate" to void the manual/drain JE (the bill JE will be kept).'
                    : ' Resolution available for amex sources only in this version.'}
                </Alert>
              )}

              {/* Show restore button for any voided JE that was created by Phase B v1 */}
              {drawerData.journal_entries
                .filter(j => j.is_deleted === 1 && (j.deleted_reason || '').startsWith('phase_b_v1_amex_dup_void'))
                .map(j => (
                  <Alert key={j.je_id} severity="info" sx={{ mt: 1 }}
                    action={
                      <Button size="small" startIcon={<RestoreIcon />} disabled={resolveBusy}
                        onClick={() => handleRestore(j.je_id)}>
                        Undo
                      </Button>
                    }>
                    JE #{j.je_id} was voided via Phase B duplicate cleanup.
                  </Alert>
                ))}

              {resolveError && <Alert severity="error" sx={{ mt: 2 }}>{resolveError}</Alert>}
              {resolveSuccess && <Alert severity="success" sx={{ mt: 2 }}>{resolveSuccess}</Alert>}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Phase B v2: repost-or-void resolution dialog. Server picks action; UI renders. */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resolvePreview?.action === 'repost' ? 'Repost canonical journal entry' : 'Resolve duplicate journal entries'}
        </DialogTitle>
        <DialogContent>
          {!resolvePreview && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}
          {resolvePreview && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {resolvePreview.action === 'repost' ? (
                  <>
                    Both existing journal entries (bill + drain) will be voided and a single fresh
                    canonical JE will be posted by the now-fixed amex processor. No drain trail remains.
                    Action is reversible (restore endpoint un-voids both old JEs and removes the new one).
                  </>
                ) : (
                  <>
                    Voiding the JE that credits the wrong Amex liability account, keeping the one
                    with the correct Cr per <code>connector_gl_mappings</code>. The processor could not
                    re-classify this transaction (no rule match) so the existing canonical JE is preserved.
                  </>
                )}
              </DialogContentText>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Action</Typography>
                    <Chip size="small"
                      color={resolvePreview.action === 'repost' ? 'primary' : 'warning'}
                      label={resolvePreview.action === 'repost' ? 'Repost canonical' : 'Void wrong-Cr only'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Will void</Typography>
                    <Typography variant="body2" fontWeight="600">
                      {(resolvePreview.would_void_je_ids || []).map(id => `JE #${id}`).join(', ')}
                    </Typography>
                  </Box>
                  {resolvePreview.would_keep_je_id && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Will keep</Typography>
                      <Typography variant="body2" fontWeight="600">JE #{resolvePreview.would_keep_je_id}</Typography>
                    </Box>
                  )}
                  {resolvePreview.would_post_new && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Will post new</Typography>
                      <Typography variant="body2" fontWeight="600">
                        Dr {resolvePreview.would_post_new.dr_account} / Cr {resolvePreview.would_post_new.cr_account} ·
                        {' '}{resolvePreview.would_post_new.amount} {resolvePreview.would_post_new.currency}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Canonical Cr</Typography>
                    <Typography variant="body2" fontWeight="600">{resolvePreview.canonical_cr}</Typography>
                  </Box>
                  <Divider />
                  <Typography variant="caption" color="text.secondary">Decision</Typography>
                  <Typography variant="caption" sx={{ pl: 1 }}>{resolvePreview.decision}</Typography>
                </Stack>
              </Paper>

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Reason (required, min 8 chars)"
                placeholder={resolvePreview.action === 'repost'
                  ? 'e.g. amex Cr-routing fix repost 2026-05'
                  : 'e.g. void wrong-Cr drain trail 2026-05'}
                value={resolveReason}
                onChange={(e) => setResolveReason(e.target.value)}
              />
            </>
          )}
          {resolveError && <Alert severity="error" sx={{ mt: 2 }}>{resolveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)} disabled={resolveBusy}>Cancel</Button>
          <Button
            onClick={confirmResolve}
            variant="contained"
            color={resolvePreview?.action === 'repost' ? 'primary' : 'error'}
            disabled={resolveBusy || !resolveReason || resolveReason.trim().length < 8}
            startIcon={resolveBusy ? <CircularProgress size={14} /> : <ResolveIcon />}
          >
            {resolvePreview?.action === 'repost' ? 'Repost canonical' : 'Void duplicate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
